import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, CalendarMonth, CheckCircle, Delete, Edit, Event, People, Refresh, Schedule, VideoCall } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import employeeMeetingService from '../../services/api/employeeMeetingService';
import employeeService from '../../services/api/employeeService';
import { friendlyError } from '../../utils/userMessages';
import {
  WorkspaceEmpty,
  WorkspaceHeader,
  WorkspacePage,
  WorkspacePanel,
  WorkspaceStatCard,
  StatusChip,
  alpha,
  dialogPaperSx,
  fieldSx,
  primaryButtonSx,
  quietButtonSx,
  workspacePalette as P,
} from '../common/AdminWorkspace';

const empty = { title: '', agenda: '', startAt: '', duration: 30, attendeeIds: [], departmentId: '', recurring: false, reminderMinutes: 15 };
const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatMeetingDate = (value) => {
  const time = toMillis(value);
  if (!time) return 'Instant / date not set';
  return new Date(time).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const employeeName = (row = {}) => row.displayName || row.email || 'Employee';

export default function EmployeeMeetings() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meetingRows, employeeRows, departmentRows] = await Promise.all([
        employeeMeetingService.listEmployeeMeetings(), employeeService.listEmployees(), employeeService.listDepartments(),
      ]);
      setMeetings(meetingRows);
      setEmployees(employeeRows.filter((row) => row.status !== 'inactive' && row.isActive !== false));
      setDepartments(departmentRows.filter((row) => row.isActive !== false));
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to load employee meetings.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      upcoming: meetings.filter((row) => row.status === 'scheduled' && (!toMillis(row.startAt) || toMillis(row.startAt) >= now)).length,
      live: meetings.filter((row) => row.status === 'live').length,
      completed: meetings.filter((row) => row.status === 'completed').length,
      invited: new Set(meetings.flatMap((row) => Array.isArray(row.attendeeIds) ? row.attendeeIds : [])).size,
    };
  }, [meetings]);

  const employeeMap = useMemo(() => Object.fromEntries(employees.map((row) => [row.id, row])), [employees]);

  const openCreate = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const edit = (meeting) => {
    if (meeting.status !== 'scheduled') { toast.error('Only scheduled meetings can be edited.'); return; }
    setEditing(meeting);
    setForm({ ...empty, ...meeting, attendeeIds: [...(meeting.attendeeIds || [])] });
    setOpen(true);
  };

  const save = async () => {
    if (!String(form.title || '').trim()) { toast.error('Meeting title is required.'); return; }
    if (Number(form.duration || 0) < 5) { toast.error('Meeting duration must be at least 5 minutes.'); return; }
    setSaving(true);
    try {
      if (editing) await employeeMeetingService.updateEmployeeMeeting(editing.id, form);
      else await employeeMeetingService.createEmployeeMeeting(form);
      toast.success(editing ? 'Meeting updated.' : 'Meeting scheduled.');
      setOpen(false);
      setEditing(null);
      setForm({ ...empty });
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to save meeting.'));
    } finally {
      setSaving(false);
    }
  };

  const start = async (meeting) => {
    try {
      if (meeting.status !== 'live') await employeeMeetingService.startEmployeeMeeting(meeting.id);
      navigate(`/employee-room/${meeting.id}`);
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to start meeting.'));
    }
  };

  const remove = async (meeting) => {
    if (!window.confirm(`Remove “${meeting.title}” from the employee meeting schedule?`)) return;
    try {
      await employeeMeetingService.deleteEmployeeMeeting(meeting.id);
      toast.success('Meeting removed from the schedule.');
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to remove meeting.'));
    }
  };

  const meetingColor = (status) => status === 'live' ? P.green : status === 'completed' ? P.teal : status === 'cancelled' ? P.danger : P.purple;

  return (
    <WorkspacePage>
      <WorkspaceHeader
        eyebrow="Internal collaboration"
        title="Employee Meetings"
        subtitle="Schedule and run TELCOFAIR employee-only video rooms with browser WebRTC, screen sharing, attendance and chat."
        icon={<VideoCall />}
        accent={P.purple}
        action={<><Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={quietButtonSx}>Refresh</Button><Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={primaryButtonSx}>Schedule meeting</Button></>}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', lg: 'repeat(4,minmax(0,1fr))' }, gap: 1.05, mb: 1.35 }}>
        <WorkspaceStatCard label="All meetings" value={meetings.length} icon={<VideoCall />} accent={P.blue} hint="Internal rooms" />
        <WorkspaceStatCard label="Upcoming" value={stats.upcoming} icon={<Schedule />} accent={P.purple} hint="Scheduled ahead" delay={.03} />
        <WorkspaceStatCard label="Live now" value={stats.live} icon={<People />} accent={P.green} hint="Active rooms" delay={.06} />
        <WorkspaceStatCard label="Employees invited" value={stats.invited} icon={<Event />} accent={P.amber} hint={`${stats.completed} completed rooms`} delay={.09} />
      </Box>

      <WorkspacePanel accent={P.purple} sx={{ p: { xs: 1.15, md: 1.4 } }}>
        {loading ? (
          <Box sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}><CircularProgress size={34} /></Box>
        ) : meetings.length === 0 ? (
          <WorkspaceEmpty icon={<VideoCall />} title="No employee meetings scheduled" text="Create a room, choose TELCOFAIR employees and start the meeting when ready." actionLabel="Schedule meeting" onAction={openCreate} accent={P.purple} />
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2,minmax(0,1fr))' }, gap: 1 }}>
            {meetings.map((meeting, index) => {
              const accent = meetingColor(meeting.status);
              const invited = (meeting.attendeeIds || []).map((id) => employeeMap[id]).filter(Boolean);
              const isCompleted = meeting.status === 'completed';
              return (
                <WorkspacePanel key={meeting.id} accent={accent} hover sx={{ p: 1.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: .85, minWidth: 0 }}>
                      <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '13px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(145deg,${accent},${alpha(accent,.72)})`, boxShadow: `0 9px 20px ${alpha(accent,.23)}` }}><VideoCall fontSize="small" /></Box>
                      <Box sx={{ minWidth: 0 }}><Typography sx={{ color: P.ink, fontWeight: 860, fontSize: '.86rem' }}>{meeting.title}</Typography><Typography sx={{ color: P.inkLight, fontSize: '.62rem', mt: .25 }}>{formatMeetingDate(meeting.startAt)} · {meeting.duration || 30} min</Typography></Box>
                    </Box>
                    <StatusChip label={meeting.status || 'scheduled'} color={accent} icon={meeting.status === 'completed' ? <CheckCircle /> : undefined} />
                  </Box>

                  <Typography sx={{ mt: .85, color: P.inkSoft, fontSize: '.67rem', lineHeight: 1.55, minHeight: 32 }}>{meeting.agenda || 'Internal employee meeting'}</Typography>
                  <Divider sx={{ my: .9, borderColor: P.hairline }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: .65, alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', pl: .4 }}>{invited.slice(0, 4).map((employee, employeeIndex) => <Avatar key={employee.id} title={employeeName(employee)} sx={{ width: 27, height: 27, ml: employeeIndex ? -.65 : 0, border: '2px solid #fff', bgcolor: [P.blue, P.teal, P.purple, P.amber][employeeIndex % 4], fontSize: '.55rem', fontWeight: 850 }}>{employeeName(employee)[0]?.toUpperCase()}</Avatar>)}</Box>
                      <Box><Typography sx={{ fontSize: '.61rem', color: P.inkSoft, fontWeight: 750 }}>{invited.length} invited</Typography><Typography sx={{ fontSize: '.56rem', color: P.inkLight }}>Room code {meeting.joinCode || '—'}</Typography></Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: .4, alignItems: 'center' }}>
                      {!isCompleted && <Button size="small" variant="contained" startIcon={<VideoCall />} onClick={() => start(meeting)} sx={{ ...primaryButtonSx, minHeight: 30, px: 1.25 }}>{meeting.status === 'live' ? 'Open room' : 'Start'}</Button>}
                      {meeting.status === 'scheduled' && <IconButton size="small" onClick={() => edit(meeting)} sx={{ color: P.blue, bgcolor: alpha(P.blue,.06) }}><Edit fontSize="small" /></IconButton>}
                      <IconButton size="small" onClick={() => remove(meeting)} disabled={meeting.status === 'live'} sx={{ color: meeting.status === 'live' ? P.inkFaint : P.danger, bgcolor: alpha(P.danger,.05) }}><Delete fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                </WorkspacePanel>
              );
            })}
          </Box>
        )}
      </WorkspacePanel>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ color: P.ink, fontWeight: 850, pb: 1 }}>{editing ? 'Edit employee meeting' : 'Schedule employee meeting'}<Typography sx={{ color: P.inkLight, fontSize: '.66rem', mt: .25 }}>Choose a department to preselect its active employees, then adjust the attendee list if needed.</Typography></DialogTitle>
        <DialogContent dividers sx={{ borderColor: P.hairline, bgcolor: '#FBFDFE' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 1.1 }}>
            <TextField sx={fieldSx} label="Meeting title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField sx={fieldSx} type="datetime-local" InputLabelProps={{ shrink: true }} label="Start time" value={form.startAt || ''} onChange={(e) => setForm({ ...form, startAt: e.target.value })} />
            <TextField sx={fieldSx} type="number" label="Duration (minutes)" inputProps={{ min: 5 }} value={form.duration} onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })} />
            <TextField sx={fieldSx} type="number" label="Reminder minutes" inputProps={{ min: 0 }} value={form.reminderMinutes} onChange={(e) => setForm({ ...form, reminderMinutes: Number(e.target.value) })} />
            <TextField sx={fieldSx} label="Department" select value={form.departmentId || ''} onChange={(e) => { const departmentId = e.target.value; const members = employees.filter((employee) => employee.departmentId === departmentId).map((employee) => employee.id); setForm({ ...form, departmentId, attendeeIds: departmentId ? members : form.attendeeIds }); }}><MenuItem value="">All / custom</MenuItem>{departments.map((department) => <MenuItem key={department.id} value={department.id}>{department.name}</MenuItem>)}</TextField>
            <TextField sx={fieldSx} label="Employees" select SelectProps={{ multiple: true }} value={form.attendeeIds || []} onChange={(e) => setForm({ ...form, attendeeIds: e.target.value })}>{employees.map((employee) => <MenuItem key={employee.id} value={employee.id}>{employeeName(employee)}</MenuItem>)}</TextField>
          </Box>
          <TextField sx={{ ...fieldSx, mt: 1.1 }} fullWidth multiline minRows={3} label="Agenda" value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} />
          <FormControlLabel sx={{ mt: .7 }} control={<Switch checked={Boolean(form.recurring)} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />} label={<Typography sx={{ fontSize: '.68rem', fontWeight: 700, color: P.inkSoft }}>Recurring meeting flag</Typography>} />
        </DialogContent>
        <DialogActions sx={{ p: 1.4 }}><Button onClick={() => setOpen(false)} sx={quietButtonSx}>Cancel</Button><Button variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <CalendarMonth />} onClick={save} sx={primaryButtonSx}>Save meeting</Button></DialogActions>
      </Dialog>
    </WorkspacePage>
  );
}
